import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { Switch } from "@/core/components/ui/switch";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateProductCode } from "@/features/ledger/api/use-create-product-code";
import { useUpdateProductCode } from "@/features/ledger/api/use-update-product-code";
import type { ProductCodeDto } from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { ProductCodeRow } from "@/features/ledger/components/product-codes/product-codes-columns";
import {
  createProductCodeSchema,
  type ProductCodeFormValues,
} from "@/features/ledger/schemas/product-code.schema";
import { PRODUCT_CATEGORIES } from "@/features/shared/api/enums";

const EMPTY_VALUES: ProductCodeFormValues = {
  code: "",
  label: "",
  category: "WALLET",
  isActive: true,
};

const toPayload = (values: ProductCodeFormValues): ProductCodeDto => ({
  code: values.code.trim(),
  label: values.label.trim(),
  category: values.category,
  isActive: values.isActive,
});

/**
 * Formulaire de création / mise à jour d'un code produit
 * (`POST /ProductCodes/add`, `PUT /ProductCodes/{id}`, guide §6.4) —
 * réservé à flashpay-superadmin / flashpay-admin. Preview ShareablePreview
 * avant submit (pas de flux d'argent → pas de section frais).
 */
export function ProductCodeFormDialog({
  productCode,
  open,
  onOpenChange,
}: {
  /** Code produit à éditer — absent = création. */
  productCode?: ProductCodeRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createProductCodeSchema(), [i18n.language]);

  const create = useCreateProductCode();
  const update = useUpdateProductCode();
  const pending = create.isPending || update.isPending;
  const [preview, setPreview] = useState<ProductCodeDto | null>(null);

  const form = useForm({
    defaultValues: productCode
      ? {
          code: productCode.code,
          label: productCode.label,
          category: productCode.category,
          isActive: productCode.isActive ?? true,
        }
      : EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    if (productCode) {
      update.mutate(
        { productCodeId: productCode.id, body: preview },
        {
          onSuccess: () => {
            notifSilence(t("ledger.productCodes.feedback.updated"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        }
      );
    } else {
      create.mutate(preview, {
        onSuccess: () => {
          notifSilence(t("ledger.productCodes.feedback.created"));
          setPreview(null);
          onOpenChange(false);
        },
        onError: (error) => notifSilenceError(error.message),
      });
    }
  };

  const fieldError =
    (name: keyof ProductCodeFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {productCode
                ? t("ledger.productCodes.edit")
                : t("ledger.productCodes.create")}
            </DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="code"
              validators={{ onChange: fieldError("code") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="code">
                    {t("ledger.productCodes.form.code")}
                  </Label>
                  <Input
                    id="code"
                    className="notranslate"
                    placeholder={t("ledger.productCodes.form.codePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="label"
              validators={{ onChange: fieldError("label") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="label">
                    {t("ledger.productCodes.form.label")}
                  </Label>
                  <Input
                    id="label"
                    className="notranslate"
                    placeholder={t("ledger.productCodes.form.labelPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="category">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("ledger.productCodes.form.category")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as ProductCodeFormValues["category"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_CATEGORIES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="isActive">
              {(field) => (
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="isActive"
                    checked={field.state.value}
                    onCheckedChange={(v) => field.handleChange(v)}
                  />
                  <Label htmlFor="isActive">
                    {t("ledger.productCodes.form.isActive")}
                  </Label>
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={
            productCode
              ? t("ledger.productCodes.preview.updateTitle")
              : t("ledger.productCodes.preview.createTitle")
          }
          description={t("ledger.productCodes.preview.description")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !pending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: pending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
